import { useParams } from "react-router-dom";
import { CalenderIcon, HomeIcon, NUSDIcon } from "../../AssetComponents/Images";
import { Toggle } from "../../Components/Toggle/Toggle";
import { useDispatch, useSelector } from "react-redux";
import { api } from "../functions/Api";
import { updateNFTStore } from "../../ReduxSlices/NFTSlice";
import Modal from "react-responsive-modal";
import { useEffect, useState } from "react";
import { DateRangePicker } from "react-date-range";
import "react-date-range/dist/styles.css"; // main css file
import "react-date-range/dist/theme/default.css"; // theme css file

import addDays from "date-fns/addDays";
import { setReservationPeriod } from "../../ReduxSlices/ReservationSlice";
import { BlackButton } from "../Buttons/BlackButton";
import { PurpleButton } from "../Buttons/PurpleButton";
import { Mainnet } from "@nibiruchain/nibijs";
import { updateToken } from "../functions/Functions";
import { isSameDayBookingAllowed } from "../utils/DateValidation";

export const PickDate = () => {
  const params = useParams();
  const dispatch = useDispatch();
  const token = useSelector((state) => state.nft.nfts[params.id]);

  const mainnet = Mainnet();
  const getToken = async () => {
    if (!token) {
      try {
        const propertyData = await api("property/getProperty", {
          token_id: params.id,
        });
        if (propertyData) {
          dispatch(
            updateNFTStore({
              token_id: params.id,
              metaData: propertyData.metaData,
              short: propertyData.short,
              rentals: propertyData.rentals,
              access: propertyData.access,
              icalSync: propertyData.icalSync || null,
              manuallyBlockedDates: propertyData.manuallyBlockedDates || [],
            })
          );
        }
      } catch (error) {
        console.error("Failed to get property from backend:", error);
        await updateToken(
          {
            token_id: params.id,
            contract: process.env.REACT_APP_RENTAL_SMART_CONTRACT,
          },
          mainnet.endptTm,
          dispatch
        );
      }
    }
  };

  useEffect(() => {
    getToken();
  }, []);

  const reservation_period = useSelector((state) => state.reservation.period);
  const [disabledDates, setDisabledDates] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [firstDay, setFirstDay] = useState();
  const diffToUTC = useSelector((state) => state.time.diffToUTC);
  
  // Helper function to get default dates (ensuring at least 1 night stay)
  const getDefaultDates = () => {
    const now = new Date();
    const currentHour = now.getHours();
    
    // If it's before 11 PM (23:00), start from today; otherwise start from tomorrow
    const startDate = currentHour < 23 ? new Date() : addDays(new Date(), 1);
    
    // End date is always at least 1 day after start date
    const endDate = addDays(startDate, 1);
    
    return { startDate, endDate };
  };
  
  const defaultPeriod = [
    {
      startDate: reservation_period.start
        ? new Date(reservation_period.start * 1000)
        : getDefaultDates().startDate,
      endDate: reservation_period.end
        ? new Date(reservation_period.end * 1000)
        : getDefaultDates().endDate,
      key: "selection",
      color: "#5b1deeaa",
    },
  ];

  // Calendar state
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('dates'); // 'dates' or 'months'
  const [selectedStartDate, setSelectedStartDate] = useState(() => {
    // Ensure start date is initialized with at least today
    return defaultPeriod[0].startDate;
  });
  const [selectedEndDate, setSelectedEndDate] = useState(() => {
    // Ensure end date is at least 1 day after start date
    const start = defaultPeriod[0].startDate;
    const end = defaultPeriod[0].endDate;
    return end > start ? end : addDays(start, 1);
  });
  const [selectionMode, setSelectionMode] = useState('start'); // 'start' or 'end'
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());

  const setFirstAvailableDay = () => {
    const today = new Date();
    
    // Check if same-day booking is allowed
    if (!isSameDayBookingAllowed(today)) {
      // If same-day booking is not allowed, start from tomorrow
      return addDays(today, 1);
    }
    
    if (disabledDates.length === 0) return today;
    for (let i = 0; i < 10000; i++) {
      if (addDays(today, i + 1) < disabledDates[0]) return addDays(today, i);
      for (let j = 0; j < disabledDates.length - 1; j++) {
        if (
          disabledDates[j] < addDays(today, i) &&
          addDays(today, i + 1) < disabledDates[j + 1]
        )
          return addDays(today, i + 1);
      }
      if (addDays(today, i) > disabledDates[disabledDates.length - 1])
        return addDays(today, i);
    }
  };

  const setRangeFromStartToEnd = (start, end) => {
    const range = [];
    range.push(new Date(start));
    let diff = 1;
    while (addDays(new Date(start), diff) <= new Date(end)) {
      range.push(addDays(new Date(start), diff));
      diff++;
    }
    return range;
  };

  // TODO: Re-implement blocked dates loading after fixing the infinite loop issue
  useEffect(() => {
    setFirstDay(setFirstAvailableDay());
  }, [disabledDates]);

  const [period, setPeriod] = useState(defaultPeriod);

  useEffect(() => {
    dispatch(
      setReservationPeriod({
        start: Math.floor(new Date(period[0].startDate).getTime() / 1000),
        end: Math.floor(new Date(period[0].endDate).getTime() / 1000),
      })
    );
  }, [period]);

  // Calendar functions
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    // Adjust for Monday as first day of week (0 = Monday, 6 = Sunday)
    const adjustedStartingDay = startingDayOfWeek === 0 ? 6 : startingDayOfWeek - 1;
    
    return { daysInMonth, startingDayOfWeek: adjustedStartingDay };
  };

  const getPreviousMonth = () => {
    const prevMonth = new Date(currentDate);
    prevMonth.setMonth(prevMonth.getMonth() - 1);
    // Compare only year and month, not the full date
    const today = new Date();
    const prevYear = prevMonth.getFullYear();
    const prevMonthIndex = prevMonth.getMonth();
    const currentYear = today.getFullYear();
    const currentMonthIndex = today.getMonth();
    
    // Allow navigation if previous month is not before current month
    if (prevYear > currentYear || (prevYear === currentYear && prevMonthIndex >= currentMonthIndex)) {
      setCurrentDate(prevMonth);
    }
  };

  const getNextMonth = () => {
    const nextMonth = new Date(currentDate);
    nextMonth.setMonth(nextMonth.getMonth() + 1);
    setCurrentDate(nextMonth);
  };

  const handleDateClick = (day) => {
    const clickedDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
    
    if (selectionMode === 'start') {
      setSelectedStartDate(clickedDate);
      // If end date is before or equal to start date, set end date to at least 1 day after
      if (clickedDate >= selectedEndDate) {
        setSelectedEndDate(addDays(clickedDate, 1));
      }
      setSelectionMode('end');
    } else {
      // When selecting end date, ensure it's at least 1 day after start date
      if (clickedDate > selectedStartDate) {
        setSelectedEndDate(clickedDate);
        setSelectionMode('start');
      } else {
        // If clicked date is before or same as start date, make it the new start date
        // and set end date to 1 day after
        setSelectedStartDate(clickedDate);
        setSelectedEndDate(addDays(clickedDate, 1));
        setSelectionMode('end');
      }
    }
  };

  const handleApply = () => {
    const newPeriod = [{
      startDate: selectedStartDate,
      endDate: selectedEndDate,
      key: "selection",
      color: "#5b1deeaa",
    }];
    setPeriod(newPeriod);
    setShowModal(false);
  };

  const handleMonthSelect = (month) => {
    setSelectedMonth(month);
    const newDate = new Date(selectedYear, month, 1);
    setCurrentDate(newDate);
    setViewMode('dates');
  };

  const handleYearSelect = (year) => {
    setSelectedYear(year);
    const newDate = new Date(year, selectedMonth, 1);
    setCurrentDate(newDate);
  };

  const formatMonthYear = (date) => {
    return date.toLocaleDateString('en-US', { 
      month: 'long', 
      year: 'numeric' 
    });
  };

  const isDateSelected = (day) => {
    const dateToCheck = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
    const isStartDate = selectedStartDate && 
           dateToCheck.getDate() === selectedStartDate.getDate() &&
           dateToCheck.getMonth() === selectedStartDate.getMonth() &&
           dateToCheck.getFullYear() === selectedStartDate.getFullYear();
    
    const isEndDate = selectedEndDate && 
           dateToCheck.getDate() === selectedEndDate.getDate() &&
           dateToCheck.getMonth() === selectedEndDate.getMonth() &&
           dateToCheck.getFullYear() === selectedEndDate.getFullYear();
    
    const isInRange = selectedStartDate && selectedEndDate &&
           dateToCheck >= selectedStartDate && dateToCheck <= selectedEndDate;
    
    return isStartDate || isEndDate || isInRange;
  };

  const isDateStart = (day) => {
    const dateToCheck = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
    return selectedStartDate && 
           dateToCheck.getDate() === selectedStartDate.getDate() &&
           dateToCheck.getMonth() === selectedStartDate.getMonth() &&
           dateToCheck.getFullYear() === selectedStartDate.getFullYear();
  };

  const isDateEnd = (day) => {
    const dateToCheck = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
    return selectedEndDate && 
           dateToCheck.getDate() === selectedEndDate.getDate() &&
           dateToCheck.getMonth() === selectedEndDate.getMonth() &&
           dateToCheck.getFullYear() === selectedEndDate.getFullYear();
  };

  const isDateDisabled = (day) => {
    const dateToCheck = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
    dateToCheck.setHours(0, 0, 0, 0); // Normalize to start of day
    
    // Check if date is in the past
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (dateToCheck < today) {
      return true;
    }
    
    // Check if date is in disabledDates array (blocked by bookings or external platforms)
    const isDisabled = disabledDates.some(disabledDate => {
      const disabledDay = new Date(disabledDate);
      disabledDay.setHours(0, 0, 0, 0); // Normalize to start of day
      const matches = disabledDay.getDate() === dateToCheck.getDate() &&
             disabledDay.getMonth() === dateToCheck.getMonth() &&
             disabledDay.getFullYear() === dateToCheck.getFullYear();
      return matches;
    });
    
    return isDisabled;
  };

  const { daysInMonth, startingDayOfWeek } = getDaysInMonth(currentDate);

  // Get previous month's days to fill the grid
  const getPreviousMonthDays = () => {
    const prevMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 0);
    const daysInPrevMonth = prevMonth.getDate();
    const days = [];
    for (let i = startingDayOfWeek - 1; i >= 0; i--) {
      days.push(daysInPrevMonth - i);
    }
    return days;
  };

  const previousMonthDays = getPreviousMonthDays();

  return (
    <>
      {/* Price and Available From Section - Responsive */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 sm:gap-0">
        <div>
          <div className="font-semibold text-sm sm:text-base">Price</div>
          <div className="flex items-center gap-[6px] sm:gap-[8px]">
            <div className="scale-75 sm:scale-100">
              <NUSDIcon />
            </div>
            <div className="text-[#5b1dee] text-[18px] sm:text-[20px]">
              {token?.short?.price_per_day}
            </div>
            <div className="text-sm sm:text-base">USDC</div>
            <div className="text-[#959595] text-sm sm:text-base">/night</div>
          </div>
        </div>
        <div className="space-y-[4px] text-center sm:text-right">
          <div className="p-[6px] w-max mx-auto sm:mx-0 rounded-full shadow-[-2px_-2px_6px_0px_rgba(253,255,255,0.80),2px_2px_6px_0px_rgba(187,195,206,0.60)]">
            <HomeIcon />
          </div>
          <div className="font-semibold text-sm sm:text-base">Available from</div>
          <div className="font-normal text-sm sm:text-base">
            {firstDay?.toLocaleDateString("en-US", {
              year: "numeric",
              month: "short",
              day: "numeric",
            })}
          </div>
        </div>
      </div>

      {/* Auto Approval Section - Responsive */}
      <div className="flex justify-center sm:justify-end">
        <div className="space-y-[10px] flex flex-col items-center sm:items-end">
          <div className="flex items-center gap-[8px] sm:gap-[10px]">
            <div className="font-semibold text-sm sm:text-base">Auto approval</div>
            <div className="scale-75 sm:scale-100">
              <Toggle status={token?.short?.auto_approve} disableHover={true} />
            </div>
          </div>
          <div className="text-center sm:text-right text-xs sm:text-sm max-w-[300px] sm:max-w-none">
            {token?.short?.auto_approve
              ? "Your rental will be activated immediately once you make reservation"
              : "Your rental will be activated once host approve your reservation"}
          </div>
        </div>
      </div>

      {/* Check-in/Check-out Section - Responsive */}
      <div className="flex flex-col sm:flex-row justify-between gap-[10px] sm:gap-[20px] w-full">
        <div className="w-full space-y-[10px]">
          <div className="font-semibold text-sm sm:text-base">Check in</div>
          <div
            onClick={() => setShowModal(true)}
            className="cursor-pointer hover:bg-[#f6f6f6] px-[12px] flex justify-between text-[#5b1dee] w-full py-[12px] sm:py-[14px] rounded-[16px] bg-white shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)]"
          >
            <div className="text-sm sm:text-base">
              {new Date(period[0]?.startDate)?.toLocaleDateString("en-US", {
                year: "numeric",
                month: "short",
                day: "numeric",
              })}
            </div>
            <div className="scale-75 sm:scale-100">
              <CalenderIcon />
            </div>
          </div>
        </div>
        <div className="w-full space-y-[10px]">
          <div className="font-semibold text-sm sm:text-base">Check out</div>
          <div
            onClick={() => setShowModal(true)}
            className="cursor-pointer hover:bg-[#f6f6f6] px-[12px] flex justify-between text-[#5b1dee] w-full py-[12px] sm:py-[14px] rounded-[16px] bg-white shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)]"
          >
            <div className="text-sm sm:text-base">
              {new Date(period[0].endDate)?.toLocaleDateString("en-US", {
                year: "numeric",
                month: "short",
                day: "numeric",
              })}
            </div>
            <div className="scale-75 sm:scale-100">
              <CalenderIcon />
            </div>
          </div>
        </div>
      </div>

      {/* Functional Calendar Modal */}
      <Modal
        open={showModal}
        center
        onClose={() => setShowModal(false)}
        showCloseIcon={false}
        classNames={{
          modal: "w-[95vw] max-w-[400px] rounded-[8px] border-[1px] border-[#E3E3E3] shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)] p-0 overflow-hidden bg-white",
          overlay: "z-[9999999]",
          modalContainer: "z-[9999999]",
        }}
        styles={{
          modal: {
            zIndex: 9999999,
          },
          overlay: {
            zIndex: 9999999,
          },
        }}
      >
        <div className="w-full h-full max-h-[80vh] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-white">
            <button
              onClick={() => setShowModal(false)}
              className="p-2 rounded-full hover:bg-gray-100 touch-manipulation"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <h2 className="text-lg font-semibold">Select Dates</h2>
            <div className="w-10"></div>
          </div>

          {/* Toggle between Dates and Months */}
          <div className="p-4">
            <div className="flex bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setViewMode('dates')}
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                  viewMode === 'dates' 
                    ? 'bg-white text-gray-900 shadow-sm' 
                    : 'text-gray-600'
                }`}
              >
                <div className="flex items-center justify-center gap-2">
                  <span>Dates</span>
                  {viewMode === 'dates' && (
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  )}
                </div>
              </button>
              <button
                onClick={() => setViewMode('months')}
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                  viewMode === 'months' 
                    ? 'bg-white text-gray-900 shadow-sm' 
                    : 'text-gray-600'
                }`}
              >
                Months
              </button>
            </div>
          </div>

          {/* Content based on view mode */}
          {viewMode === 'dates' ? (
            <>
              {/* Calendar Navigation */}
              <div className="flex items-center justify-between px-4 mb-4">
                <button
                  onClick={getPreviousMonth}
                  className="p-2 rounded-full hover:bg-gray-100 touch-manipulation"
                  disabled={(() => {
                    const prevMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1);
                    const today = new Date();
                    const prevYear = prevMonth.getFullYear();
                    const prevMonthIndex = prevMonth.getMonth();
                    const currentYear = today.getFullYear();
                    const currentMonthIndex = today.getMonth();
                    return prevYear < currentYear || (prevYear === currentYear && prevMonthIndex < currentMonthIndex);
                  })()}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <h3 className="text-lg font-semibold">{formatMonthYear(currentDate)}</h3>
                <button
                  onClick={getNextMonth}
                  className="p-2 rounded-full hover:bg-gray-100 touch-manipulation"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>

              {/* Selection Mode Indicator */}
              <div className="px-4 mb-4">
                <div className="text-sm text-[#666666] text-center">
                  {selectionMode === 'start' ? 'Select check-in date' : 'Select check-out date'}
                </div>
              </div>

              {/* Calendar Grid */}
              <div className="flex-1 px-4 pb-6 overflow-y-auto">
                {/* Days of week header */}
                <div className="grid grid-cols-7 gap-1 mb-2">
                  {['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sat', 'Su'].map((day) => (
                    <div key={day} className="text-center text-sm text-gray-500 py-2">
                      {day}
                    </div>
                  ))}
                </div>

                {/* Calendar grid */}
                <div className="grid grid-cols-7 gap-1">
                  {/* Previous month days */}
                  {previousMonthDays.map((day) => (
                    <div key={`prev-${day}`} className="text-center py-2 text-gray-300">
                      {day}
                    </div>
                  ))}

                  {/* Current month days */}
                  {Array.from({ length: daysInMonth }, (_, i) => i + 1).map((day) => {
                    const isSelected = isDateSelected(day);
                    const isStart = isDateStart(day);
                    const isEnd = isDateEnd(day);
                    const isDisabled = isDateDisabled(day);
                    const dateToCheck = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
                    const isPast = dateToCheck < new Date();
                    const isBlocked = !isPast && isDisabled; // Blocked by booking/external platform
                    
                    return (
                      <button
                        key={day}
                        onClick={() => !isDisabled && handleDateClick(day)}
                        disabled={isDisabled}
                        className={`relative text-center py-2 rounded-full transition-colors touch-manipulation ${
                          isStart || isEnd
                            ? 'bg-[#5b1dee] text-white'
                            : isSelected
                            ? 'bg-[#5b1dee] bg-opacity-20 text-[#5b1dee]'
                            : isDisabled
                            ? 'text-gray-300 cursor-not-allowed'
                            : 'text-gray-900 hover:bg-gray-100'
                        }`}
                      >
                        {day}
                        {/* Strikethrough line for blocked dates (Airbnb style) */}
                        {isBlocked && (
                          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                            <div className="w-[70%] h-[1px] bg-gray-400 transform rotate-[-45deg]"></div>
                          </div>
                        )}
                      </button>
                    );
                  })}

                  {/* Next month days to fill the grid */}
                  {(() => {
                    const totalCells = 42; // 6 rows * 7 columns
                    const usedCells = previousMonthDays.length + daysInMonth;
                    const remainingCells = Math.max(0, totalCells - usedCells);
                    
                    return Array.from({ length: remainingCells }, (_, i) => (
                      <div key={`next-${i + 1}`} className="text-center py-2 text-gray-300">
                        {i + 1}
                      </div>
                    ));
                  })()}
                </div>
              </div>
            </>
          ) : (
            /* Months and Years Selection */
            <div className="flex-1 px-4 pb-6 overflow-y-auto">
              {/* Year Selection */}
              <div className="mb-6">
                <h4 className="text-lg font-semibold mb-3">Year</h4>
                <div className="grid grid-cols-3 gap-2">
                  {Array.from({ length: 5 }, (_, i) => selectedYear - 2 + i).map((year) => (
                    <button
                      key={year}
                      onClick={() => handleYearSelect(year)}
                      className={`py-3 px-4 rounded-lg text-center font-medium transition-colors ${
                        year === selectedYear
                          ? 'bg-[#5b1dee] text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {year}
                    </button>
                  ))}
                </div>
              </div>

              {/* Month Selection */}
              <div>
                <h4 className="text-lg font-semibold mb-3">Month</h4>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    'January', 'February', 'March',
                    'April', 'May', 'June',
                    'July', 'August', 'September',
                    'October', 'November', 'December'
                  ].map((month, index) => (
                    <button
                      key={month}
                      onClick={() => handleMonthSelect(index)}
                      className={`py-3 px-4 rounded-lg text-center font-medium transition-colors ${
                        index === selectedMonth && selectedYear === currentDate.getFullYear()
                          ? 'bg-[#5b1dee] text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {month}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Apply Button */}
          <div className="p-4 border-t border-gray-200">
            <button
              onClick={handleApply}
              className="w-full bg-gradient-to-b from-[#5b1deec0] from-0% to-70% to-[#5b1dee] text-white py-4 px-6 rounded-[12px] font-semibold text-lg shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)] hover:shadow-[-1px_6px_10px_0_rgba(93,0,207,0.5)] hover:translate-y-[-3px] active:bg-[#5b1dee] touch-manipulation flex items-center justify-center gap-2 transition-all duration-200"
            >
              Apply
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
};
